// src/components/admin/AdminBookings.js
import React, { useState, useEffect } from 'react';
import {
  Search,
  Filter,
  Calendar,
  User,
  Car,
  MapPin,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp,
  Edit3,
  Save,
  X
} from 'lucide-react';
import AdminService from '../../services/AdminService';

const AdminBookings = ({ isMobile }) => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);
  const [editingNotes, setEditingNotes] = useState(null);
  const [tempStatus, setTempStatus] = useState('');
  const [tempNotes, setTempNotes] = useState('');

  useEffect(() => {
    // Subscribe to all bookings in real-time
    const unsubscribe = AdminService.subscribeToAllBookings((updatedBookings) => {
      setBookings(updatedBookings);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    // Filter bookings based on search and filters
    let filtered = bookings;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(booking =>
        booking.service?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.bookingId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.vehicle?.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.vehicle?.model?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking =>
        (booking.status || 'pending').toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(booking => booking.bookingType === typeFilter);
    }

    setFilteredBookings(filtered);
  }, [bookings, searchTerm, statusFilter, typeFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStatusEdit = (booking) => {
    setEditingStatus(booking.bookingId);
    setTempStatus(booking.status || 'pending');
  };

  const handleNotesEdit = (booking) => {
    setEditingNotes(booking.bookingId);
    setTempNotes(booking.notes || '');
  };

  const saveStatus = async (booking) => {
    try {
      await AdminService.updateBookingStatus(booking, tempStatus);
      setEditingStatus(null);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const saveNotes = async (booking) => {
    try {
      await AdminService.updateBookingNotes(booking, tempNotes);
      setEditingNotes(null);
    } catch (error) {
      console.error('Error updating notes:', error);
      alert('Failed to update notes');
    }
  };

  const toggleBookingDetails = (bookingId) => {
    setExpandedBooking(expandedBooking === bookingId ? null : bookingId);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">All Bookings</h2>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search bookings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-[#3A98B9] focus:border-[#3A98B9]"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#3A98B9] focus:border-[#3A98B9]"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-[#3A98B9] focus:border-[#3A98B9]"
          >
            <option value="all">All Types</option>
            <option value="user">Registered Users</option>
            <option value="guest">Guest Bookings</option>
          </select>

          <div className="text-sm text-gray-600 flex items-center">
            <Filter className="h-4 w-4 mr-1" />
            {filteredBookings.length} of {bookings.length} bookings
          </div>
        </div>
      </div>

      {/* Bookings List */}
      <div className="space-y-4">
        {filteredBookings.length > 0 ? (
          filteredBookings.map((booking) => {
            const isExpanded = expandedBooking === booking.bookingId;

            return (
              <div key={booking.bookingId} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Booking Header */}
                <div className="bg-gray-50 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                        {booking.status || 'Pending'}
                      </span>
                      <span className="font-medium">{booking.service?.title}</span>
                      <span className="text-sm text-gray-500">
                        {booking.bookingType === 'guest' ? '(Guest)' : '(Registered)'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">
                        #{booking.bookingId.slice(-6).toUpperCase()}
                      </span>
                      <button
                        onClick={() => toggleBookingDetails(booking.bookingId)}
                        className="text-[#3A98B9] hover:text-[#2A87A8]"
                      >
                        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Booking Summary */}
                <div className="px-6 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-start space-x-3">
                      <User className="h-5 w-5 text-[#3A98B9] mt-0.5" />
                      <div>
                        <p className="font-medium">{booking.userName}</p>
                        <p className="text-sm text-gray-600">{booking.userEmail}</p>
                        {booking.userInfo?.phone && (
                          <p className="text-sm text-gray-600">{booking.userInfo.phone}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Calendar className="h-5 w-5 text-[#3A98B9] mt-0.5" />
                      <div>
                        <p className="font-medium">{formatDate(booking.schedule?.date)}</p>
                        <p className="text-sm text-gray-600">{booking.schedule?.time}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Car className="h-5 w-5 text-[#3A98B9] mt-0.5" />
                      <div>
                        <p className="font-medium">
                          {booking.vehicle?.year} {booking.vehicle?.make} {booking.vehicle?.model}
                        </p>
                        <p className="text-sm text-gray-600">
                          {formatCurrency(booking.payment?.total || booking.service?.price)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-200 px-6 py-4 bg-white">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Customer & Vehicle Details */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">Customer & Vehicle Details</h4>

                        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                          <div className="flex items-center space-x-2">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <span className="text-sm">{booking.userEmail}</span>
                          </div>
                          {booking.userInfo?.phone && (
                            <div className="flex items-center space-x-2">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span className="text-sm">{booking.userInfo.phone}</span>
                            </div>
                          )}
                          <div className="flex items-center space-x-2">
                            <MapPin className="h-4 w-4 text-gray-400" />
                            <span className="text-sm">{booking.vehicle?.location || 'Not specified'}</span>
                          </div>
                          {booking.vehicle?.color && (
                            <div className="text-sm">
                              <span className="text-gray-600">Color: </span>
                              {booking.vehicle.color}
                            </div>
                          )}
                          {booking.vehicle?.instructions && (
                            <div className="text-sm">
                              <span className="text-gray-600">Instructions: </span>
                              {booking.vehicle.instructions}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Admin Controls */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">Admin Controls</h4>

                        {/* Status Control */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Status</span>
                            {editingStatus === booking.bookingId ? (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => saveStatus(booking)}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <Save className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setEditingStatus(null)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleStatusEdit(booking)}
                                className="text-[#3A98B9] hover:text-[#2A87A8]"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                            )}
                          </div>

                          {editingStatus === booking.bookingId ? (
                            <select
                              value={tempStatus}
                              onChange={(e) => setTempStatus(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#3A98B9] focus:border-[#3A98B9]"
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          ) : (
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                              {booking.status || 'Pending'}
                            </span>
                          )}
                        </div>

                        {/* Notes Control */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Admin Notes</span>
                            {editingNotes === booking.bookingId ? (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => saveNotes(booking)}
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <Save className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => setEditingNotes(null)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleNotesEdit(booking)}
                                className="text-[#3A98B9] hover:text-[#2A87A8]"
                              >
                                <Edit3 className="h-4 w-4" />
                              </button>
                            )}
                          </div>

                          {editingNotes === booking.bookingId ? (
                            <textarea
                              value={tempNotes}
                              onChange={(e) => setTempNotes(e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#3A98B9] focus:border-[#3A98B9]"
                              placeholder="Add admin notes..."
                            />
                          ) : (
                            <p className="text-sm text-gray-600">
                              {booking.notes || 'No notes added'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No bookings have been made yet'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBookings;